import React, {useEffect, useState} from 'react'
import {Helmet} from "react-helmet-async";
import Header from '../components/header'
import Footer from '../components/footer'
import Hero from "../components/Hero/hero";
import './home.css'
import Timeline from "../components/Timeline/timeline";
import PrimaryButton from "../components/PrimaryButton";
import SecondaryButton from "../components/SecondaryButton";
import {useTranslation} from "react-i18next";
import ProblemsSolutions from "../components/problems-solutions/problems-solutions";
import CaseStudySlider from "../components/CaseStudySlider/CaseStudySlider";
import {useLocation} from "react-router-dom";

const Home = ({showCalModal, hideCalModal}) => {
    const location = useLocation();

    // Track current theme (light/dark) to switch assets accordingly
    const getInitialTheme = () => {
        try {
            const root = document.documentElement;
            const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            const savedTheme = localStorage.getItem('theme');
            const attrTheme = root.getAttribute('data-theme');
            return attrTheme || savedTheme || (prefersDark ? 'dark' : 'light');
        } catch (e) {
            return 'light';
        }
    };
    const [theme, setTheme] = useState(getInitialTheme);

    useEffect(() => {
        const root = document.documentElement;
        // Observe changes to data-theme so UI reacts when toggled
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((m) => {
                if (m.attributeName === 'data-theme') {
                    const current = root.getAttribute('data-theme');
                    if (current) setTheme(current);
                }
            });
        });
        observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.substring(1));
            if (element) {
                element.scrollIntoView({behavior: 'smooth'});
            }
        }
    }, [location]);

    const {t} = useTranslation();

    const footerMenuItems = [
        <a href="/#home-section-home">
            <span className="Large">{t("footer_menu-home")}</span>
        </a>,
        <a href="/#problems-solutions"><span className="Large">{t("footer_menu-problems-solutions")}</span></a>,
        <a href="/#profile-section">
            <span className="Large">{t("footer_menu-about-me")}</span>
        </a>,
        <a href={"/legal"}>
            <span className="Large">{t("footer_menu-impressum")}</span>
        </a>,
        <a href={"/privacy"}>
            <span className="Large">{t("footer_menu-privacy")}</span>
        </a>
    ]

    return (
        <div className="home-container">
            <Helmet>
                <title>Louis Nguyen - Full Stack Developer & MVP Builder</title>
                <meta name="description"
                      content="Do you already have a clear vision for your startup or product, but lack the technical implementation? As an experienced software developer, I will transform your idea into customized, market-ready software solutions. Let's lay the foundation for your sustainable success together!"/>
                <meta property="og:title" content="Louis Nguyen - Full Stack Developer & MVP Builder"/>
                <meta property="og:description"
                      content="Do you already have a clear vision for your startup or product, but lack the technical implementation? As an experienced software developer, I will transform your idea into customized, market-ready software solutions. Let's lay the foundation for your sustainable success together!"/>
            </Helmet>

            <Header showCalModal={showCalModal}></Header>

            <section id="home-section-home" className="home-hero">
                <Hero showCalModal={showCalModal}/>
            </section>

            {/* Show background image only in dark theme */}
            {theme === 'dark' && (
                <img alt="Futuristic background" src="/futuristic_silver_lining.png"
                     className="home-bio-background-image"/>
            )}


            <div id="profile-section"></div>
            <section className="home-about-me-container">
                <div className="container">
                    <div className="about-me-content">
                        <h2 className="section-title">
                            <span>{t("home_about-me-heading")}</span>
                        </h2>
                        <p className="about-me-text text-body-large">
                            {t("home_about-me-text")}
                        </p>

                        <div className="social-links">
                            <a className="social-media-link" href="https://www.linkedin.com/in/louis-nguyen-073836202/"
                               aria-label="LinkedIn Profile">
                                <img alt="LinkedIn" src="/linkedin-svgrepo-com.svg" className="social-icon"/>
                            </a>
                            <a className="social-media-link"
                               href="https://www.xing.com/profile/Louis_Nguyen3/web_profiles" aria-label="Xing Profile">
                                <img alt="Xing" src="/xing-fill-svgrepo-com.svg" className="social-icon"/>
                            </a>
                            <SecondaryButton
                                button={t("home_button-cv")}
                                action={() => (window.location.href = "/CV.pdf")}
                                variant="outline"
                            />
                        </div>
                    </div>
                </div>

                <CaseStudySlider/>

                <div className="container">
                    <div className="about-me-content">
                        <div className="career-timeline">
                            <Timeline timelineElements={[
                                {
                                    id: 4,
                                    title: t("home_about-me-timeline-4-title"),
                                    text: t("home_about-me-timeline-4-text"),
                                    date: "2017 - 2020",
                                },
                                {
                                    id: 3,
                                    title: t("home_about-me-timeline-3-title"),
                                    text: t("home_about-me-timeline-3-text"),
                                    date: "2021",
                                },
                                {
                                    id: 2,
                                    title: t("home_about-me-timeline-2-title"),
                                    text: t("home_about-me-timeline-2-text"),
                                    date: "2021 - 2022",
                                    images: ["/badee-1.jpg", "/badee-2.jpg", "/badee-3.jpg"]
                                },
                                {
                                    id: 1,
                                    title: t("home_about-me-timeline-1-title"),
                                    text: <span
                                        dangerouslySetInnerHTML={{__html: t("home_about-me-timeline-1-text")}}/>,
                                    date: "2022 - 2024",
                                }
                            ]}/>
                        </div>
                    </div>
                </div>
            </section>

            <section id="problems-solutions" className="home-section-services">
                <div className="container">
                    <h2 className="section-title">
                        <span>{t("home_home-problems-solutions-heading")}</span>
                    </h2>
                    <ProblemsSolutions/>
                </div>
            </section>

            <section className="home-steps-section">
                <div className="container">
                    <div className="steps-content">
                        <div className="steps-intro">
                            <h2 className="heading-2">{t("home_steps-heading")}</h2>
                            <p className="text-lead">
                                {t("home_steps-text")}
                            </p>
                            <p className="text-body">
                                {t("home_steps-consequences")}
                            </p>
                            <p className="text-body">
                                {t("home_steps-message")}
                            </p>
                            <p className="steps-signature">{t("home_steps-signature")}</p>
                        </div>

                        <div className="steps-container">
                            <div className="step-card">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h3 className="heading-4">{t("home_step1-heading")}</h3>
                                    <p className="text-body">{t("home_step1-text")}</p>
                                </div>
                            </div>
                            <div className="step-card">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h3 className="heading-4">{t("home_step2-heading")}</h3>
                                    <p className="text-body">{t("home_step2-text")}</p>
                                </div>
                            </div>
                            <div className="step-card">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <h3 className="heading-4">{t("home_step3-heading")}</h3>
                                    <p className="text-body">{t("home_step3-text")}</p>
                                </div>
                            </div>
                            <div className="step-card">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <h3 className="heading-4">{t("home_step4-heading")}</h3>
                                    <p className="text-body">{t("home_step4-text")}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="contact-cta">
                        <div className="cta-content">
                            <img alt="Rocket launch" src={theme === 'light' ? '/rocket.svg' : '/rocket_white.svg'}
                                 className="cta-icon"/>
                            <h2 className="heading-2">{t("home_contact-heading")}</h2>
                            <div className="cta-buttons">
                                <PrimaryButton
                                    button={t("home_button-book-call")}
                                    action={showCalModal}
                                    size="lg"
                                />
                                <SecondaryButton
                                    button={t("home_button-text-me")}
                                    action={() => window.location.href = "mailto:business@louisnguyen.de"}
                                    size="lg"
                                    variant="outline"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer footerMenuItems={footerMenuItems}></Footer>
        </div>
    )
}

export default Home